import React, { useContext, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { SecurityFlowDataContext } from "../../SecurityTestingPage";
import DesignPhase from "../../../input-components/security-testing/DesignPhase";
import SecondFunctionalModules from "../../../output-components/security-testing/SecondFunctionalModules";
import { Box, Container, Typography } from "@mui/material";
import { ReactComponent as AutomateIcon } from "../../../../images/automateIcon.svg";
import FullscreenToggle from "../../../custom-components/FullScreenToggle";

const DesignOutputPage = () => {
  const location = useLocation();
  const {
    funcMod,
    setFuncMod,
    securityReq,
    setSecurityReq,
    archDesign,
    setArchDesign,
    secRecommendations,
    setSecRecommendations,
    threatModelling,
    setThreatModelling,
    addReview,
    setAddReview,
    securityTestPlan,
    setSecurityTestPlan,
  } = useContext(SecurityFlowDataContext);

  const hasSetSecurityReqRef = useRef(false);

  useEffect(() => {
    setFuncMod(location.state?.funcMod);
    setSecurityReq(location.state?.securityReq);
    setArchDesign(location.state?.archDesign);
    setSecRecommendations(location.state?.secRecommendations);
    setThreatModelling(location.state?.threatModelling);
    setAddReview(location.state?.addReview);
    setSecurityTestPlan(location.state?.securityTestPlan);
  }, [
    location.state?.funcMod || {},
    location.state?.securityReq || {},
    location.state?.archDesign || {},
    location.state?.secRecommendations || {},
    location.state?.threatModelling || {},
    location.state?.addReview || {},
    location.state?.securityTestPlan || {},
  ]);

  const hasDesignInputs = useMemo(() => {
    return (
      (funcMod && Object.keys(funcMod).length > 0) ||
      (securityReq && Object.keys(securityReq).length > 0) ||
      (archDesign && Object.keys(archDesign).length > 0)
    );
  }, [funcMod, securityReq, archDesign]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "center",
        width: "100%", // Always full width
        minHeight: "80vh", // Fill vertical space
        bgcolor: "#f6f3f2",
        overflowY: "auto", // Allow scrolling if needed
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#888",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          backgroundColor: "#555",
        },
        px: { xs: 2, sm: 3, md: 4 }, // Responsive side padding
        boxSizing: "border-box",
      }}
    >
      <Container
        sx={{
          width: "100%", // Full width inside parent
          maxWidth: "100%", // Never overflow
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          alignItems: "center", // Center content horizontally
          "& > *:not(:last-child)": {
            mb: 2, // Add vertical gap between sections
          },
          mb: 6,
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center">
            <AutomateIcon style={{ marginRight: 4 }} />
            <Typography
              variant="body2"
              color="text.secondary"
              gutterBottom
              sx={{ mt: 0.5 }}
            >
              Generate fast and automated security checks
            </Typography>
          </Box>
          <FullscreenToggle />
        </Box>

        <DesignPhase />
        {hasDesignInputs && <SecondFunctionalModules />}
      </Container>

      {/* Sticky footer */}
      <Box
        component="footer"
        sx={{
          width: "100%",
          backgroundColor: "#f6f3f2",
          textAlign: "center",
          px: 2,
          py: 1,
          position: "fixed",
          bottom: 0,
          zIndex: 99,
        }}
      >
        <Typography variant="caption" color="textSecondary">
          Use QMentisAI™ with human supervision, as with all generative AI
          tools.
        </Typography>
      </Box>
    </Box>
  );
};

export default DesignOutputPage;
